import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { retry, catchError, switchMap } from 'rxjs/operators';

import { ErrorDetails } from '@shared/models';
import { HealthService } from '@shared/services/http-client/health.service';
import { TokenService } from '@shared/services/token.service';

import { AuthService } from '../services/auth.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<ErrorDetails>> {
    return next.handle(request).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => {
        const translate = this.injector.get(TranslateService);
        const tokenService = this.injector.get(TokenService);
        const healthService = this.injector.get(HealthService);

        if (error.status === 0 || error.status >= 500) {
          healthService.markForCheck();
        }

        console.error(error);
        let errorDetails: ErrorDetails;
        if (!error.error) {
          errorDetails = {
            statusCode: error.status,
            message: translate.instant('error.serverError'),
            details: null!,
            logId: null!,
            errors: null!,
          };
        } else {
          errorDetails = error.error;
        }

        if (error.status == 401 && tokenService.isUserLoggedIn()) {
          return this.handleTokenExpired(request, next);
        }

        if (error.status === 0 && error.error instanceof ProgressEvent) {
          errorDetails.message = translate.instant('error.noInternetConnection');
        }

        return throwError(() => errorDetails);
      })
    );
  }

  private handleTokenExpired(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<ErrorDetails>> {
    const authService = this.injector.get(AuthService);
    if (request.url.includes('auth/refresh-token')) {
      authService.logout();
      return of(null);
    }
    return authService.refreshToken('').pipe(
      switchMap(() => {
        return next.handle(this.addToken(request, authService.getAuthorizationToken()));
      }),
      catchError((error) => {
        authService.logout();
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
